import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";

export interface SignalResearchCitation {
  id: number;
  label: string;
  url: string | null;
}

export interface SignalResearchAnswer {
  answer: string;
  citations: SignalResearchCitation[];
  model: string;
  generated_at: string;
}

interface ResearchEnvelope {
  data: { research: SignalResearchAnswer };
}

async function errorMessage(error: unknown): Promise<string> {
  const context = (error as { context?: Response }).context;
  if (context && typeof context.json === "function") {
    try {
      const parsed = (await context.json()) as {
        error?: { message?: string } | string;
        message?: string;
      };
      if (typeof parsed.error === "object" && parsed.error?.message) {
        return parsed.error.message;
      }
      if (typeof parsed.error === "string") return parsed.error;
      if (parsed.message) return parsed.message;
    } catch {
      // The SDK message below remains the honest fallback for non-JSON errors.
    }
  }
  return error instanceof Error ? error.message : String(error);
}

export async function requestSignalResearch(options: {
  signalId: string;
  question: string;
}): Promise<SignalResearchAnswer> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke("research_signal_assistant", {
      body: {
        signal_id: options.signalId,
        question: options.question,
      },
    }),
    "research assistant request",
    SUPABASE_EDGE_TIMEOUT_MS
  );
  if (error) throw new Error(await errorMessage(error));
  const research = (data as ResearchEnvelope | null)?.data?.research;
  if (!research?.answer) {
    throw new Error("The research service returned an incomplete answer.");
  }
  return research;
}
